import type { FC, ReactElement, ReactNode, RefObject } from "react"

import { AnimatePresence, type MotionProps, motion, useReducedMotion } from "framer-motion"

import { ErrorIcon } from "@/3514/assets"
import { ETypographyColor, ETypographySize, Typography } from "@/3514/components"
import { THEME } from "@/3514/theme"
import { buildCssClassName } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

type TVariant = "error-secondary" | "error" | "info"

type TPosition = "center" | "left"

type TProps = {
    ref?: RefObject<HTMLDivElement>
    id?: string
    isVisible: boolean
    isBorderless?: boolean
    variant?: TVariant
    position?: TPosition
    text?: string
    containerClassName?: string
    role?: "alert"
    motionProps?: MotionProps
    withNoAnimatePresence?: boolean
}

const classes = {
    container: ({ isBorderless }: { isBorderless: boolean }): string =>
        buildCssClassName("flex", "gap-x-[8px]", "items-center", "w-fit", "justify-center", "py-[5px]", "px-[10px]", [
            isBorderless || ["border-[1px]", "rounded-[5px]"]
        ])
}

const bgColorsMap: { [key in TVariant]: string } = {
    error: "bg-transparent",
    "error-secondary": "bg-red-500",
    info: "bg-transparent"
}

const borderColorsMap: { [key in TVariant]: string } = {
    error: "border-red-200",
    "error-secondary": "border-transparent",
    info: "border-blue-200"
}

const positionMap: { [key in TPosition]: string } = {
    center: "[margin:0_auto]",
    left: "[margin-right:auto]"
}

const iconColorMap: { [key in TVariant]: string } = {
    error: THEME.COLORS.accentOld,
    "error-secondary": THEME.COLORS.accentOld,
    info: THEME.COLORS.blue[200]
}

const textColorMap: { [key in TVariant]: ETypographyColor } = {
    error: ETypographyColor.Gray23,
    "error-secondary": ETypographyColor.Dark,
    info: ETypographyColor.Dark
}

const ErrorHint: FC<TProps> = ({
    isVisible,
    ref = null,
    id = undefined,
    isBorderless = false,
    position = "center",
    variant = "info",
    text = undefined,
    containerClassName = undefined,
    motionProps = undefined,
    role = undefined,
    withNoAnimatePresence = false
}: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const isMotionReduced: boolean = useReducedMotion()

    const container: ReactNode = (
        <motion.div
            ref={ref}
            id={id}
            aria-live="polite"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: isMotionReduced ? 0 : 0.2 }}
            className={buildCssClassName(classes.container({ isBorderless }), {
                [borderColorsMap[variant]]: variant,
                [bgColorsMap[variant]]: variant,
                [positionMap[position]]: position,
                [containerClassName]: containerClassName
            })}
            {...(role && { role })}
            {...motionProps}
        >
            <ErrorIcon fillColor={iconColorMap[variant]} />

            <Typography
                isPretty
                text={text ?? t("common.errorHint")}
                color={textColorMap[variant]}
                size={ETypographySize.Tiny}
            />
        </motion.div>
    )

    return withNoAnimatePresence ? (
        isVisible ? (
            container
        ) : null
    ) : (
        <AnimatePresence initial={false} mode="wait">
            {isVisible ? container : null}
        </AnimatePresence>
    )
}

ErrorHint.displayName = "ErrorHint"

export { ErrorHint }
